/**
 * เปิดใบขอซื้อ (PR) แบบหัวกระดาษ + หลายรายการ — D-173
 *
 * เลขที่ `PR-<โรงงาน>-YYMMXXXX` เซิร์ฟเวอร์เป็นผู้ออก (นับแยกต่อโรงงาน เริ่มใหม่ทุกเดือน) รายการแต่ละบรรทัดมีรหัส `-001`, `-002` …
 * โรงงานเป็น dropdown จากโรงงานที่มีสิทธิ์ (`plant_scope`) ช่อง item ค้นหา Part Number แบบ Select2 และ
 * เพิ่ม item ใหม่ได้ทันทีถ้ายังไม่เคยมี (สร้าง Product Master ขั้นต่ำสถานะ DRAFT) Part Number ไม่ซ้ำตาม DB
 * คู่ขนานกับ `PurchaseRequisitionScreen` (D-084) ซึ่งไม่ถูกแตะ
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  createProduct,
  fetchProductList,
  fetchProductTypes,
  fetchUoms,
} from '../../lib/products/client';
import type { Product, ProductTypeEntry, UomEntry } from '../../lib/products/types';
import {
  createPurchaseRequest,
  fetchPurchaseRequest,
  fetchPurchaseRequests,
  transitionPurchaseRequest,
  type PurchaseRequest,
  type PurchaseRequestFailure,
} from '../../lib/procurement/purchaseRequest';
import { newIdempotencyKey } from '../../lib/inventory/stock';
import { selectablePlants } from '../productionOrders/productionOrderView';

const PROCUREMENT_MODULE = 'PROCUREMENT';
const PRODUCT_MODULE = 'PRODUCT_MASTER';
const SEARCH_LIMIT = 20;
const POSITIVE_DECIMAL = /^\d+(\.\d{1,4})?$/;

export interface PurchaseRequestScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl?: string;
}

interface LineDraft {
  readonly key: number;
  readonly product: Product | null;
  readonly quantity: string;
}

function isPositiveQuantity(text: string): boolean {
  return POSITIVE_DECIMAL.test(text.trim()) && Number(text) > 0;
}

function failureText(failure: PurchaseRequestFailure): string {
  switch (failure.status) {
    case 'unauthenticated':
      return 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่';
    case 'forbidden':
      return 'ไม่มีสิทธิ์ทำรายการนี้ หรือโรงงานนี้อยู่นอกขอบเขตของคุณ';
    case 'rejected':
      return `${failure.message ?? 'เซิร์ฟเวอร์ปฏิเสธคำขอ'}${failure.rule ? ` (${failure.rule})` : ''}`;
    case 'server-error':
      return `เซิร์ฟเวอร์ผิดพลาด (${failure.httpStatus})`;
    case 'network-error':
      return `เชื่อมต่อไม่ได้: ${failure.detail}`;
    case 'invalid-response':
      return 'คำตอบจากเซิร์ฟเวอร์ผิดรูป';
  }
}

/* ------------------------------------------------------- deep-link detail -- */

interface LinkedRequestProps {
  readonly prId: string;
  readonly apiBaseUrl?: string;
  readonly onClose: () => void;
}

/** แสดงใบเดียวจาก `?pr_id=` — หา/สิทธิ์ไม่ได้แสดงข้อความ ไม่กระทบรายการด้านล่าง */
function LinkedRequest({ prId, apiBaseUrl, onClose }: LinkedRequestProps) {
  const [state, setState] = useState<
    { readonly kind: 'loading' } | { readonly kind: 'ready'; readonly pr: PurchaseRequest } | { readonly kind: 'failed'; readonly text: string }
  >({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    setState({ kind: 'loading' });
    void fetchPurchaseRequest(prId, apiBaseUrl === undefined ? {} : { apiBaseUrl }).then((result) => {
      if (!active) {
        return;
      }
      if (result.status === 'ready') {
        setState({ kind: 'ready', pr: result.request });
      } else if (result.status === 'rejected' && result.httpStatus === 404) {
        setState({ kind: 'failed', text: 'ไม่พบใบขอซื้อนี้ หรืออยู่นอกโรงงานที่คุณมีสิทธิ์เห็น' });
      } else {
        setState({ kind: 'failed', text: failureText(result) });
      }
    });
    return () => {
      active = false;
    };
  }, [prId, apiBaseUrl]);

  if (state.kind === 'loading') {
    return (
      <p className="cap-notice__text" role="status" data-testid="prq-detail-loading">
        กำลังโหลดใบขอซื้อ…
      </p>
    );
  }
  if (state.kind === 'failed') {
    return (
      <div className="cap-notice" role="alert" data-testid="prq-detail-error">
        <p className="cap-notice__text">{state.text}</p>
        <button type="button" className="cap-button cap-button--ghost" onClick={onClose}>
          ปิด
        </button>
      </div>
    );
  }
  const { pr } = state;
  return (
    <section className="cap-notice" data-testid="prq-detail" aria-label={`รายละเอียด ${pr.request_no}`}>
      <h2 className="cap-notice__title">
        <code className="cap-table__code">{pr.request_no}</code>{' '}
        <span data-testid="prq-detail-status">{pr.status}</span>
      </h2>
      <p className="cap-notice__text">
        โรงงาน {pr.plant_code} · ผู้สร้าง {pr.created_by_employee_id}
        {pr.note !== null ? ` · ${pr.note}` : ''}
      </p>
      <table className="cap-table" data-testid="prq-detail-lines">
        <thead>
          <tr>
            <th>รหัสรายการ</th>
            <th>สินค้า</th>
            <th>จำนวน</th>
            <th>หน่วย</th>
          </tr>
        </thead>
        <tbody>
          {pr.lines.map((l) => (
            <tr key={l.line_code}>
              <td>
                <code className="cap-table__code">{l.line_code}</code>
              </td>
              <td>
                {l.product_code} {l.product_name}
              </td>
              <td>{l.quantity}</td>
              <td>{l.uom_code}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="cap-button cap-button--ghost" onClick={onClose}>
        ปิด
      </button>
    </section>
  );
}

/* ------------------------------------------------------------ item picker -- */

interface NewItemFormProps {
  readonly partNo: string;
  readonly plantCode: string;
  readonly apiBaseUrl?: string;
  readonly onCreated: (product: Product) => void;
  readonly onCancel: () => void;
}

/** สร้าง Product Master ขั้นต่ำ (DRAFT): Part Number + ชื่อ + ประเภท + หน่วยนับ */
function NewItemForm({ partNo, plantCode, apiBaseUrl, onCreated, onCancel }: NewItemFormProps) {
  const [name, setName] = useState('');
  const [productType, setProductType] = useState('');
  const [baseUom, setBaseUom] = useState('');
  const [types, setTypes] = useState<readonly ProductTypeEntry[]>([]);
  const [uoms, setUoms] = useState<readonly UomEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const options = apiBaseUrl === undefined ? {} : { apiBaseUrl };

  useEffect(() => {
    let active = true;
    void fetchProductTypes({ status: 'ACTIVE' }, options).then((result) => {
      if (active && result.status === 'ready') {
        setTypes(result.page.items);
      }
    });
    void fetchUoms(options).then((result) => {
      if (active && result.status === 'ready') {
        setUoms(result.page.items);
      }
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (name.trim() === '' || productType === '' || baseUom === '') {
      setError('กรอกชื่อ เลือกประเภท และหน่วยนับให้ครบ');
      return;
    }
    setBusy(true);
    setError(null);
    const result = await createProduct(
      {
        product_code: partNo,
        name: name.trim(),
        product_type: productType,
        base_uom: baseUom,
        plant_data: [{ plant_code: plantCode, valuation_class: '0' }],
      },
      options,
    );
    setBusy(false);
    if (result.status === 'ready') {
      onCreated(result.product);
      return;
    }
    setError(
      result.status === 'conflict'
        ? `Part Number ${partNo} มีอยู่แล้ว — ค้นหาแล้วเลือกจากรายการ`
        : result.status === 'forbidden'
          ? 'ไม่มีสิทธิ์สร้าง Product Master'
          : 'สร้าง item ไม่สำเร็จ ลองใหม่อีกครั้ง',
    );
  };

  return (
    <form className="cap-form" data-testid="pr-new-item-form" onSubmit={(e) => void submit(e)}>
      <h3 className="cap-notice__title">เพิ่ม item ใหม่ (Product Master ร่าง)</h3>
      <label className="cap-field">
        <span className="cap-field__label">Part Number</span>
        <input className="cap-input" value={partNo} readOnly />
      </label>
      <label className="cap-field">
        <span className="cap-field__label">ชื่อ item</span>
        <input
          className="cap-input"
          aria-label="ชื่อ item"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className="cap-field">
        <span className="cap-field__label">ประเภท</span>
        <select
          className="cap-input"
          aria-label="ประเภท item"
          value={productType}
          onChange={(e) => setProductType(e.target.value)}
        >
          <option value="">— เลือก —</option>
          {types.map((t) => (
            <option key={t.type_code} value={t.type_code}>
              {t.type_code} — {t.name}
            </option>
          ))}
        </select>
      </label>
      <label className="cap-field">
        <span className="cap-field__label">หน่วยนับ</span>
        <select
          className="cap-input"
          aria-label="หน่วยนับ item"
          value={baseUom}
          onChange={(e) => setBaseUom(e.target.value)}
        >
          <option value="">— เลือก —</option>
          {uoms.map((u) => (
            <option key={u.uom_code} value={u.uom_code}>
              {u.uom_code} — {u.name}
            </option>
          ))}
        </select>
      </label>
      {error !== null && (
        <p className="cap-notice__note" role="alert" data-testid="pr-new-item-error">
          {error}
        </p>
      )}
      <div className="cap-form__actions">
        <button type="submit" className="cap-button cap-button--primary" disabled={busy}>
          {busy ? 'กำลังสร้าง…' : 'สร้างและเลือก item นี้'}
        </button>
        <button type="button" className="cap-button cap-button--ghost" onClick={onCancel}>
          ยกเลิก
        </button>
      </div>
    </form>
  );
}

interface ItemPickerProps {
  readonly product: Product | null;
  readonly plantCode: string;
  readonly canCreateItem: boolean;
  readonly apiBaseUrl?: string;
  readonly onChange: (product: Product | null) => void;
}

/** ช่องเลือก item แบบ Select2: พิมพ์ค้นหา Part Number/ชื่อ → เลือก หรือเพิ่ม item ใหม่ถ้าไม่เคยมี */
function ItemPicker({ product, plantCode, canCreateItem, apiBaseUrl, onChange }: ItemPickerProps) {
  const [text, setText] = useState('');
  const [results, setResults] = useState<readonly Product[]>([]);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const latest = useRef(0);

  useEffect(() => {
    const q = text.trim();
    if (q === '') {
      setResults([]);
      return;
    }
    const ticket = ++latest.current;
    void fetchProductList(
      { q, limit: SEARCH_LIMIT },
      apiBaseUrl === undefined ? {} : { apiBaseUrl },
    ).then((result) => {
      if (ticket === latest.current) {
        setResults(result.status === 'ready' ? result.page.items : []);
      }
    });
  }, [text, apiBaseUrl]);

  if (product !== null) {
    return (
      <span data-testid="pr-item-selected">
        <code className="cap-table__code">{product.product_code}</code> {product.name}{' '}
        <button
          type="button"
          className="cap-button cap-button--ghost"
          aria-label={`เปลี่ยน item ${product.product_code}`}
          onClick={() => onChange(null)}
        >
          เปลี่ยน
        </button>
      </span>
    );
  }

  const partNo = text.trim().toUpperCase();
  const exact = results.some((p) => p.product_code.toUpperCase() === partNo);

  if (adding) {
    return (
      <NewItemForm
        partNo={partNo}
        plantCode={plantCode}
        {...(apiBaseUrl === undefined ? {} : { apiBaseUrl })}
        onCreated={(created) => {
          setAdding(false);
          onChange(created);
        }}
        onCancel={() => setAdding(false)}
      />
    );
  }

  return (
    <div className="cap-field">
      <input
        className="cap-input"
        role="combobox"
        aria-expanded={open}
        aria-label="ค้นหา item (Part Number / ชื่อ)"
        placeholder="พิมพ์ Part Number หรือชื่อ"
        value={text}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
      />
      {open && text.trim() !== '' && (
        <ul role="listbox" className="cap-listbox" data-testid="pr-item-options">
          {results.map((p) => (
            <li
              key={p.product_id}
              role="option"
              aria-selected={false}
              tabIndex={0}
              onClick={() => {
                onChange(p);
                setOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onChange(p);
                  setOpen(false);
                }
              }}
            >
              <code className="cap-table__code">{p.product_code}</code> {p.name} ({p.base_uom})
            </li>
          ))}
          {!exact && (
            <li role="option" aria-selected={false}>
              {canCreateItem ? (
                <button type="button" className="cap-button cap-button--ghost" onClick={() => setAdding(true)}>
                  ＋ เพิ่ม item ใหม่ “{partNo}”
                </button>
              ) : (
                <span>ไม่พบ item นี้ และไม่มีสิทธิ์เพิ่ม Product Master</span>
              )}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ screen -- */

export function PurchaseRequestScreen({ permissions, apiBaseUrl }: PurchaseRequestScreenProps) {
  const plants = selectablePlants(permissions);
  const canCreate = can(permissions, PROCUREMENT_MODULE, 'CREATE');
  const canCreateItem = can(permissions, PRODUCT_MODULE, 'CREATE');
  const canApprove = can(permissions, PROCUREMENT_MODULE, 'APPROVE');
  const options = apiBaseUrl === undefined ? {} : { apiBaseUrl };
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedPrId = searchParams.get('pr_id')?.trim() ?? '';

  const [plantCode, setPlantCode] = useState('');
  const [note, setNote] = useState('');
  const [deliverTo, setDeliverTo] = useState('');
  const [lines, setLines] = useState<readonly LineDraft[]>([{ key: 1, product: null, quantity: '' }]);
  const [nextKey, setNextKey] = useState(2);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<PurchaseRequest | null>(null);
  const [recent, setRecent] = useState<readonly PurchaseRequest[]>([]);
  // หนึ่งเนื้อหาของใบ = หนึ่งคีย์: ส่งซ้ำของเดิมใช้คีย์เดิม แก้เนื้อหาแล้วได้คีย์ใหม่ สำเร็จแล้วทิ้ง (D-178)
  const submission = useRef<{ fingerprint: string; key: string } | null>(null);

  const effectivePlant = plantCode !== '' ? plantCode : plants.length === 1 ? (plants[0] ?? '') : '';

  const reload = useCallback(async () => {
    const result = await fetchPurchaseRequests({ limit: 20, ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }) });
    if (result.status === 'ready') {
      setRecent(result.items);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const transition = async (pr: PurchaseRequest, action: 'approve' | 'cancel') => {
    setError(null);
    setBusy(true);
    const result = await transitionPurchaseRequest(pr.pr_id, action, options);
    setBusy(false);
    if (result.status !== 'ready') {
      setError(failureText(result));
      return;
    }
    void reload();
  };

  const patchLine = (key: number, patch: Partial<LineDraft>) =>
    setLines((current) => current.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setCreated(null);
    if (effectivePlant === '') {
      setError('เลือกโรงงาน');
      return;
    }
    const chosen = lines.filter((l) => l.product !== null || l.quantity.trim() !== '');
    if (chosen.length === 0 || chosen.some((l) => l.product === null || !isPositiveQuantity(l.quantity))) {
      setError('ทุกรายการต้องเลือก item และกรอกจำนวนมากกว่า 0');
      return;
    }
    const codes = chosen.map((l) => (l.product as Product).product_code);
    if (new Set(codes).size !== codes.length) {
      setError('Part Number ซ้ำกันในใบเดียว — รวมเป็นรายการเดียว');
      return;
    }
    setBusy(true);
    setError(null);
    const body = {
      plant_code: effectivePlant,
      note: note.trim(),
      deliver_to_location_code: deliverTo.trim().toUpperCase(),
      lines: chosen.map((l) => ({
        product_code: (l.product as Product).product_code,
        quantity: l.quantity.trim(),
      })),
    };
    const fingerprint = JSON.stringify(body);
    if (submission.current?.fingerprint !== fingerprint) {
      submission.current = { fingerprint, key: newIdempotencyKey() };
    }
    const result = await createPurchaseRequest(body, { ...options, idempotencyKey: submission.current.key });
    setBusy(false);
    if (result.status !== 'ready') {
      setError(failureText(result));
      return;
    }
    submission.current = null;
    setCreated(result.request);
    setLines([{ key: nextKey, product: null, quantity: '' }]);
    setNextKey(nextKey + 1);
    setNote('');
    setDeliverTo('');
    void reload();
  };

  return (
    <div className="cap-page" data-testid="purchase-requests">
      <div className="cap-page__head">
        <h1 className="cap-page__title">เปิดใบขอซื้อ (PR)</h1>
      </div>

      {linkedPrId !== '' && (
        <LinkedRequest
          prId={linkedPrId}
          {...(apiBaseUrl === undefined ? {} : { apiBaseUrl })}
          onClose={() =>
            setSearchParams(
              (current) => {
                const next = new URLSearchParams(current);
                next.delete('pr_id');
                return next;
              },
              { replace: true },
            )
          }
        />
      )}

      {!canCreate && (
        <div className="cap-notice" role="status" data-testid="prq-read-only">
          <p className="cap-notice__text">
            สิทธิ์ชุดนี้ดูได้อย่างเดียว — ต้องมี <code className="cap-table__code">can_create</code> ของโมดูล{' '}
            <code className="cap-table__code">{PROCUREMENT_MODULE}</code> จึงเปิดใบได้
          </p>
        </div>
      )}

      {canCreate && (
        <form className="cap-form" data-testid="prq-form" onSubmit={(e) => void submit(e)}>
          <label className="cap-field">
            <span className="cap-field__label">โรงงาน</span>
            <select
              className="cap-input"
              aria-label="โรงงาน"
              value={effectivePlant}
              onChange={(e) => setPlantCode(e.target.value)}
            >
              <option value="">— เลือกโรงงาน —</option>
              {plants.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
          <label className="cap-field">
            <span className="cap-field__label">หมายเหตุ (ไม่บังคับ)</span>
            <input className="cap-input" aria-label="หมายเหตุ" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">สถานที่จัดส่ง — รหัสที่เก็บของโรงงานนี้ (ไม่บังคับ)</span>
            <input
              className="cap-input"
              aria-label="สถานที่จัดส่ง"
              value={deliverTo}
              onChange={(e) => setDeliverTo(e.target.value)}
            />
          </label>

          <table className="cap-table" data-testid="prq-lines">
            <thead>
              <tr>
                <th>#</th>
                <th>Item (Part Number)</th>
                <th>จำนวน</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={line.key}>
                  <td>-{String(index + 1).padStart(3, '0')}</td>
                  <td>
                    <ItemPicker
                      product={line.product}
                      plantCode={effectivePlant}
                      canCreateItem={canCreateItem && effectivePlant !== ''}
                      {...(apiBaseUrl === undefined ? {} : { apiBaseUrl })}
                      onChange={(product) => patchLine(line.key, { product })}
                    />
                  </td>
                  <td>
                    <input
                      className="cap-input"
                      inputMode="decimal"
                      aria-label={`จำนวนรายการ -${String(index + 1).padStart(3, '0')}`}
                      value={line.quantity}
                      onChange={(e) => patchLine(line.key, { quantity: e.target.value })}
                    />
                    {line.product !== null && <span> {line.product.base_uom}</span>}
                  </td>
                  <td>
                    {lines.length > 1 && (
                      <button
                        type="button"
                        className="cap-button cap-button--ghost"
                        aria-label={`ลบรายการ -${String(index + 1).padStart(3, '0')}`}
                        onClick={() => setLines((current) => current.filter((l) => l.key !== line.key))}
                      >
                        ลบ
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="cap-form__actions">
            <button
              type="button"
              className="cap-button cap-button--ghost"
              onClick={() => {
                setLines((current) => [...current, { key: nextKey, product: null, quantity: '' }]);
                setNextKey(nextKey + 1);
              }}
            >
              ＋ เพิ่มรายการ
            </button>
            <button type="submit" className="cap-button cap-button--primary" disabled={busy}>
              {busy ? 'กำลังบันทึก…' : 'เปิดใบขอซื้อ'}
            </button>
          </div>
          {error !== null && (
            <p className="cap-notice__note" role="alert" data-testid="prq-error">
              {error}
            </p>
          )}
        </form>
      )}

      {created !== null && (
        <div className="cap-notice" role="status" data-testid="prq-created">
          <h2 className="cap-notice__title">
            เปิดใบขอซื้อ <code className="cap-table__code">{created.request_no}</code> แล้ว
          </h2>
          <p className="cap-notice__text">
            {created.lines.map((l) => l.line_code).join(', ')}
          </p>
        </div>
      )}

      <table className="cap-table" data-testid="prq-recent">
        <thead>
          <tr>
            <th>เลขที่ PR</th>
            <th>โรงงาน</th>
            <th>รายการ</th>
            <th>สถานะ</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {recent.map((pr) => (
            <tr key={pr.pr_id}>
              <td>
                <code className="cap-table__code">{pr.request_no}</code>
              </td>
              <td>{pr.plant_code}</td>
              <td>
                {pr.lines.map((l) => (
                  <div key={l.line_code}>
                    <code className="cap-table__code">{l.line_code}</code> {l.product_code} × {l.quantity}{' '}
                    {l.uom_code}
                  </div>
                ))}
              </td>
              <td>{pr.status}</td>
              <td>
                {canApprove && pr.status === 'OPEN' && (
                  <button
                    type="button"
                    className="cap-button cap-button--primary"
                    disabled={busy}
                    aria-label={`อนุมัติ ${pr.request_no}`}
                    onClick={() => void transition(pr, 'approve')}
                  >
                    อนุมัติ
                  </button>
                )}{' '}
                {canApprove && (pr.status === 'OPEN' || pr.status === 'APPROVED') && (
                  <button
                    type="button"
                    className="cap-button cap-button--ghost"
                    disabled={busy}
                    aria-label={`ยกเลิก ${pr.request_no}`}
                    onClick={() => void transition(pr, 'cancel')}
                  >
                    ยกเลิก
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
